import React, { useEffect, useRef, useState } from "react";
import OnlyOfficeEditor from "../OnlyOffice/OnlyOfficeEditor";
import {
  Button,
  Col,
  Input,
  Menu,
  Popover,
  Modal,
  Row,
  Spin,
  Tooltip,
  Select,
} from "antd";
import {
  AudioMutedOutlined,
  AudioOutlined,
  CheckOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import AuthContainer from "../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../store/reducers/authReducer";
import ReportService from "../../../services/Report/report.service";
import ReportTemplateService from "../../../services/ReportTemplate/reportTemplate.service";
import AdminCaseService from "../../../services/Case/AdminCase/adminCase.service";
import DoctorCaseService from "../../../services/Case/DoctorCase/doctorCase.service";
import { Doctor } from "../../../models/Doctor/doctor.model";
import UploadDirectReportButton from "../UploadDirectReportButton";
import { getDropdownOptions } from "../../utils/dropdownUtils";
import { Report } from "../../../models/Report/report.model";
import { CaseDetail } from "../../../models/CaseDetail/caseDetail.model";
import { DownloadFileTypeEnum } from "../../../enums/downloadFileType.enum";
import { CaseStatusEnum } from "../../../enums/caseStatus.enum";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import { NotificationTypes } from "../../../enums/notificationTypes";
import Notification from "../Notification";
import ApiRoutes from "../../../routes/routeConstants/apiRoutes";
import axios from "axios";

const documentServerUrl =
  process.env.REACT_APP_ONLYOFFICE_DOCKER_RUNNING_URL || "";
const BACKEND_ONLYOFFICE_URL = process.env.REACT_APP_API_BASE_URL;

declare var MediaRecorder: any;
let socket: WebSocket | null = null;
const aiDiagnosisTypes = ["US"];

interface OnlyOfficePageProps extends AuthReducerProps {}

interface OrganApiResponse {
  data: { id: number; value: string }[];
  message: string;
  status: number;
}

// Confirm modal state shape
interface ConfirmModalState {
  visible: boolean;
  title: string;
  message: string;
  onOk: () => void;
}

const OnlyOfficePage = ({ user }: OnlyOfficePageProps) => {
  const editorInstanceRef = useRef<any>(null);

  // Extract caseId from URL (robust, no trailing slash required)
  const caseIdMatch = window.location.pathname.match(/\/cases\/(\d+)/);
  const caseDetailId = caseIdMatch ? Number(caseIdMatch[1]) : null;

  // KEY FIX: Use caseDetailId as the fixed key for the editor.
  const fixedKey = caseDetailId?.toString() || "";

  // KEY FIX: editorVersion is now a state variable, incremented on caseDetailId change.
  const [editorVersion, setEditorVersion] = useState(0);

  // Detect userRole from URL — default ADMIN, support doctor routes
  const isDoctor = window.location.pathname.includes("/doctor/");
  const userRole = isDoctor ? UserRoleEnum.DOCTOR : UserRoleEnum.ADMIN;

  // Refs — always hold latest values, zero stale closure issues
  const currentContentRef = useRef<string>("");
  const formValuesRef = useRef<any>(new Report());
  const sessionTimerRef = useRef<number>(0);
  const lastSyncedRef = useRef<number>(0);
  const isSyncingRef = useRef<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimerRef = useRef<any>(null);

  // Suppress hasUnsavedChanges when onContentChange fires from polling (not a user edit)
  const isForceSavePollingRef = useRef<boolean>(false);

  // State variables
  const [caseDetail, setCaseDetail] = useState<CaseDetail | undefined>(
    undefined,
  );
  const [formValues, setFormValues] = useState<any>(new Report());
  const [reportTemplates, setReportTemplates] = useState<any[]>([]);
  const [reportTemplateOptions, setReportTemplateOptions] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [activeHtmlContent, setActiveHtmlContent] = useState<string>("");
  const [currentContent, setCurrentContent] = useState<string>("");
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [publishLoading, setPublishLoading] = useState<boolean>(false);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [wordLoading, setWordLoading] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState<boolean>(false);
  const [usgReportLoading, setUsgReportLoading] = useState<boolean>(false);
  const [disableAiReporting, setDisableAiReporting] = useState<boolean>(false);
  const [disableUsgReporting, setDisableUsgReporting] = useState<boolean>(
    false,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [textareaValue, setTextareaValue] = useState<string>("");
  const [baseValue, setBaseValue] = useState<string>("");
  const [liveTranscript, setLiveTranscript] = useState<string>("");
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [isMicLoading, setIsMicLoading] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [voiceLoading, setVoiceLoading] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);
  const [totalTimer, setTotalTimer] = useState<number>(0);
  const [sessionTimer, setSessionTimer] = useState<number>(0);
  const [aiCreditsUsed, setAiCreditsUsed] = useState<number>(0);
  const [aiTotalCredits, setAiTotalCredits] = useState<number>(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  // Co-sign: set when the report is locked by another user — enables read-only banner
  const [cosignLockedBy, setCosignLockedBy] = useState<string | null>(null);

  // Voice/AI feature flags
  const [isVoiceToTextEnabled, setVoiceToTextEnabled] = useState<boolean>(
    false,
  );
  const [isAiReportingEnabled, setAiReportingEnabled] = useState<boolean>(
    false,
  );
  const [isAiReportingAllowed, setAiReportingAllowed] = useState<boolean>(
    false,
  );
  const [isVoiceToTextAllowed, setVoiceToTextAllowed] = useState<boolean>(
    false,
  );
  const [isDeepgram, setDeepgram] = useState<boolean>(true);

  const [isAiDiagnosisEnabled, setAiDiagnosisEnabled] = useState<boolean>(
    false,
  );
  const [selectedOrgan, setSelectedOrgan] = useState<any>(null);
  const [subOptions, setSubOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [organOptions, setOrganOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [subOptionsMap, setSubOptionsMap] = useState<{
    [key: string]: { value: string; label: string }[];
  }>({});
  const [selectedOrganValue, setSelectedOrganValue] = useState<any>(null);
  const [selectedDetailValue, setSelectedDetailValue] = useState<any>(null);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    title: "",
    message: "",
    onOk: () => {},
  });

  const showSaveConfirm = (
    title: string,
    message: string,
    onOk: () => void,
  ) => {
    setConfirmModal({ visible: true, title, message, onOk });
  };

  const handleConfirmModalOk = () => {
    setConfirmModal((prev) => ({ ...prev, visible: false }));
    confirmModal.onOk();
  };

  const handleConfirmModalCancel = () => {
    setConfirmModal((prev) => ({ ...prev, visible: false }));
  };

  //  Keep refs in sync with state
  useEffect(() => {
    currentContentRef.current = currentContent;
  }, [currentContent]);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    sessionTimerRef.current = sessionTimer;
  }, [sessionTimer]);

  // Fetch case details
  useEffect(() => {
    if (!caseDetailId) {
      setPageLoading(false);
      return;
    }
    const showMethod = isDoctor
      ? DoctorCaseService.showDoctorCase
      : AdminCaseService.showAdminCase;

    showMethod(
      caseDetailId,
      (detail: CaseDetail) => {
        if (detail.cosignEnabled) {
          // Co-sign mode: auto-assign the opening user to this case
          const cosignMethod = isDoctor
            ? DoctorCaseService.cosignAssign
            : AdminCaseService.cosignAssign;
          cosignMethod(
            caseDetailId,
            (updatedDetail: CaseDetail) => {
              setCaseDetail(updatedDetail);
            },
            (error: any) => {
              // 423 = report locked by another user — open in read-only mode
              if (error?.response?.status === 423) {
                setCaseDetail(detail);
                const lb = error?.response?.data?.locked_by;
                setCosignLockedBy(lb?.display_name ?? "Another user");
              } else {
                // Fallback: still show the case even if cosign_assign failed
                setCaseDetail(detail);
              }
            },
            () => {},
          );
        } else {
          setCaseDetail(detail);
        }
      },
      () => {},
      () => {},
    );
  }, [caseDetailId]);

  // handleFetchReportTemplates
  const handleFetchReportTemplates = (searchText: string) => {
    ReportTemplateService.fetchReportTemplates(
      searchText,
      caseDetail?.scanTypeId,
      (templates: any[]) => {
        setReportTemplates(templates);
        setReportTemplateOptions(getDropdownOptions(templates, "name", "id"));
      },
      () => {},
      () => {},
    );
  };

  // Debounced template search
  useEffect(() => {
    if (caseDetail) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        handleFetchReportTemplates(searchTerm);
      }, 500);
    }
  }, [searchTerm]);

  // setVoiceAndAiFeature
  const setVoiceAndAiFeature = () => {
    const featuresMap: Record<string, any> = {
      voice_to_text: {
        enabled: false,
        setter: setVoiceToTextEnabled,
        serviceCall: ReportService.getVoiceToTextInfo,
        updateState: (response: any) => {
          setTimer(response.data.used_credits ?? 0);
          setTotalTimer(response.data.subscribed_credits ?? 0);
          setSessionTimer(0);
          setVoiceToTextAllowed(response.data.allowed);
        },
      },
      ai_reporting: {
        enabled: false,
        setter: setAiReportingEnabled,
        serviceCall: ReportService.getAiReportingInfo,
        updateState: (response: any) => {
          setAiCreditsUsed(response.data.used_credits ?? 0);
          setAiTotalCredits(response.data.subscribed_credits ?? 0);
          setAiReportingAllowed(response.data.allowed);
        },
      },
    };

    const userFeatures =
      userRole === UserRoleEnum.ADMIN
        ? user?.adminFeatures
        : user?.doctorFeatures;

    let hasAugnito = false;

    userFeatures?.forEach((feature: any) => {
      if (featuresMap[feature.feature])
        featuresMap[feature.feature].enabled = true;
      if (feature.feature === "augnito_stt") hasAugnito = true;
    });

    setDeepgram(!hasAugnito);

    Object.values(featuresMap).forEach(
      ({ enabled, setter, serviceCall, updateState }) => {
        if (enabled) {
          serviceCall(
            userRole,
            user?.id,
            (response: any) => {
              setter(true);
              updateState(response);
            },
            () => {},
            () => {},
          );
        }
      },
    );
  };

  // Organ / pathology fetchers
  const fetchOrganOptions = () => {
    try {
      ReportService.getOrganList(
        (response: OrganApiResponse) => {
          const options = response.data.map((item: any) => ({
            value: item.value.replace(/\s+/g, "_").toLowerCase(),
            label: item.value
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c: string) => c.toUpperCase()),
          }));
          setOrganOptions(options);
        },
        (error: any) => {
          console.error("Failed to fetch organ list", error);
        },
        () => {},
      );
    } catch (error) {
      console.error("Failed to fetch organ options:", error);
    }
  };

  const fetchOrganPathologyDetails = () => {
    try {
      ReportService.getOrganPathologyDetails(
        (response: any) => {
          setSubOptionsMap(response.data);
        },
        (error: any) => {
          console.error("Failed to fetch pathology details", error);
        },
        () => {},
      );
    } catch (error) {
      console.error("Failed to fetch pathology details:", error);
    }
  };

  // When caseDetail loads → fetch report + templates + features
  useEffect(() => {
    if (!caseDetail?.id) return;

    // Fetch templates
    handleFetchReportTemplates("");

    // Fetch voice/AI features
    setVoiceAndAiFeature();

    // Fetch organ data when scan type is US
    if (aiDiagnosisTypes.includes(caseDetail?.scanTypeName as string)) {
      fetchOrganOptions();
      fetchOrganPathologyDetails();
    }

    // Fetch existing report
    const loadReportAndEditor = (
      report: Report,
      emptyFallback: boolean = false,
    ) => {
      const html = report.content || "<p></p>";
      setFormValues(report);
      formValuesRef.current = report;
      setActiveHtmlContent(html);
      setCurrentContent(html);
      currentContentRef.current = html;
      loadHtmlIntoEditor(html, true);
      setPageLoading(false);
    };

    // Fetch existing report
    if (caseDetail.status === CaseStatusEnum.TO_BE_APPROVED) {
      ReportService.showAdminReport(
        caseDetail.id,
        (report: Report) => loadReportAndEditor(report, false),
        () => loadReportAndEditor(new Report(), true),
        () => setPageLoading(false),
      );
    } else {
      ReportService.showReport(
        caseDetail.id,
        (report: Report) => loadReportAndEditor(report, false),
        () => loadReportAndEditor(new Report(), true),
        () => setPageLoading(false),
      );
    }
  }, [caseDetail]);

  // Ctrl+S → Save Draft
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        e.stopPropagation();
        if (!formValuesRef.current?.status && !cosignLockedBy) {
          handleSaveDraftWithContent(
            currentContentRef.current,
            formValuesRef.current,
          );
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []); // empty deps intentional — refs always hold latest values

  // OnlyOffice iframe Ctrl+S → set flag, actual save happens in onContentChange
  useEffect(() => {
    const handleIframeSave = (event: MessageEvent) => {
      if (event.data?.type === "ONLYOFFICE_INTERNAL_SAVE") {
        if (!formValuesRef.current?.status) {
          isForceSavePollingRef.current = true;
        }
      }
    };
    window.addEventListener("message", handleIframeSave);
    return () => window.removeEventListener("message", handleIframeSave);
  }, []);

  // Warn on tab close only if truly unsaved
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  // Voice timer interval
  useEffect(() => {
    if (!isRunning) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      setTimer((prev) => prev + 1);
      setSessionTimer((prev) => {
        const updated = prev + 1;
        if (updated - lastSyncedRef.current >= 10) syncVoiceUsage(updated);
        return updated;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Voice/AI helpers
  const syncVoiceUsage = (latestSeconds?: number) => {
    if (isSyncingRef.current || !caseDetail) return;
    const current = latestSeconds ?? sessionTimerRef.current;
    const secondsToStore = current - lastSyncedRef.current;
    if (secondsToStore < 10) return;
    isSyncingRef.current = true;
    ReportService.storeVoiceToTextSessionData(
      userRole,
      user?.id,
      secondsToStore,
      caseDetail.id,
      () => {
        lastSyncedRef.current = current;
        isSyncingRef.current = false;
      },
      () => {
        isSyncingRef.current = false;
      },
      () => {
        isSyncingRef.current = false;
      },
    );
  };

  const storeVoiceToTextSession = () => {
    if (!caseDetail) return;
    ReportService.storeVoiceToTextSessionData(
      userRole,
      user?.id,
      sessionTimerRef.current - lastSyncedRef.current,
      caseDetail.id,
      (response: any) => {
        setVoiceToTextEnabled(true);
        setTimer(response.data.used_credits);
        setTotalTimer(response.data.subscribed_credits);
        setIsMuted(true);
        setIsRunning(false);
        setSessionTimer(0);
        setVoiceToTextAllowed(response.data.allowed);
      },
      () => {},
      () => {},
    );
    setVoiceLoading(false);
  };

  const storeAiReportingSession = () => {
    if (!caseDetail) return;
    ReportService.storeAiReportingSessionData(
      userRole,
      user?.id,
      1,
      caseDetail.id,
      (response: any) => {
        setAiCreditsUsed(response.data.used_credits);
        setAiTotalCredits(response.data.subscribed_credits);
        setAiReportingAllowed(response.data.allowed);
      },
      () => {},
      () => {},
    );
  };

  // loadHtmlIntoEditor
  const loadHtmlIntoEditor = async (html: string, force = false) => {
    await axios.post(
      `${BACKEND_ONLYOFFICE_URL}/convert-html-to-docx`,
      { html, reportId: fixedKey, force }, // Fixed key + force
      { headers: { "Content-Type": "application/json" } },
    );
    setCurrentContent(html);
    currentContentRef.current = html;
    setActiveHtmlContent(html);

    // Force OnlyOffice to remount and reload the document
    setEditorVersion((prev) => prev + 1);
  };

  // New: Post-save cache refresh (overwrites DOCX cache under fixedKey)
  const refreshEditorCache = async (html: string) => {
    try {
      await axios.post(
        `${BACKEND_ONLYOFFICE_URL}/convert-html-to-docx`,
        { html, reportId: fixedKey, force: true },
        { headers: { "Content-Type": "application/json" } },
      );
      console.log("[OnlyOfficePage] Cache refreshed post-save:", fixedKey);
    } catch (err) {
      console.error("Cache refresh failed:", err);
    }
  };

  const handleSaveDraftWithContent = (
    contentToSave: string,
    currentFormValues: any,
  ) => {
    if (formLoading) return;
    setFormLoading(true);

    const report = Object.assign(new Report(), currentFormValues);
    report.content = contentToSave;
    report.caseDetailId = caseDetail?.id ?? caseDetailId ?? undefined;

    const onSuccess = (r?: Report) => {
      if (r) {
        setFormValues(r);
        formValuesRef.current = r;
      }
      // KEY: Refresh backend cache with DB-fresh content under fixedKey
      refreshEditorCache(contentToSave);
      setFormLoading(false);
      setHasUnsavedChanges(false);
    };

    const onError = () => {
      setFormLoading(false);
      Notification({
        message: "Failed to save report",
        type: NotificationTypes.ERROR,
      });
    };

    if (report.id) {
      ReportService.updateReport(report, onSuccess, onError, () => {});
    } else {
      ReportService.createReport(report, onSuccess, onError, () => {});
    }
  };

  const handleSaveDraft = () => {
    handleSaveDraftWithContent(
      currentContentRef.current,
      formValuesRef.current,
    );
  };

  const handlePublishReport = () => {
    setPublishLoading(true);
    const report = Object.assign(new Report(), formValuesRef.current);
    report.content = currentContentRef.current;
    ReportService.publishReport(
      report,
      (r: Report) => {
        setFormValues(r);
        formValuesRef.current = r;
        setHasUnsavedChanges(false);
        setPublishLoading(false);
        Notification({
          message: "Report published successfully",
          type: NotificationTypes.SUCCESS,
        });
      },
      () => {
        setPublishLoading(false);
      },
      () => {},
    );
  };

  const handleSendForApproval = () => {
    setPublishLoading(true);
    const report = Object.assign(new Report(), formValuesRef.current);
    report.content = currentContentRef.current;
    ReportService.sendForApproval(
      report,
      caseDetailId ?? 0,
      (r: Report) => {
        setFormValues(r);
        formValuesRef.current = r;
        setHasUnsavedChanges(false);
        setPublishLoading(false);
      },
      () => {
        setPublishLoading(false);
      },
      () => {},
    );
  };

  const handleApproveAndPublishReport = () => {
    setPublishLoading(true);
    const report = Object.assign(new Report(), formValuesRef.current);
    report.content = currentContentRef.current;
    ReportService.approveAndPublishReport(
      report,
      (r: Report) => {
        setFormValues(r);
        formValuesRef.current = r;
        setHasUnsavedChanges(false);
        setPublishLoading(false);
      },
      () => {
        setPublishLoading(false);
      },
      () => {},
    );
  };

  const handleViewReport = () => {
    if (!caseDetail?.id) return;
    setPdfLoading(true);
    ReportService.previewReport(
      caseDetail.id,
      formValuesRef.current?.id ?? 0,
      (data: Blob) => {
        const fileURL = window.URL.createObjectURL(
          new Blob([data], { type: "application/pdf" }),
        );
        const pdfWindow = window.open(fileURL, "_blank");
        if (pdfWindow)
          pdfWindow.onload = () => window.URL.revokeObjectURL(fileURL);
        setPdfLoading(false);
      },
      () => {
        setPdfLoading(false);
        setWordLoading(false);
      },
      () => {},
    );
  };

  const handleDownloadReport = (
    status: boolean | undefined,
    fileType: DownloadFileTypeEnum,
    withLetterHead: boolean,
    reportId?: number,
  ) => () => {
    if (!caseDetail?.id) return;
    setPdfLoading(true);
    if (status) {
      ReportService.downloadReport(
        fileType,
        caseDetail,
        withLetterHead,
        reportId ?? 0,
        (reportUrl: string) => {
          window.open(reportUrl, "_blank");
        },
        () => {},
        () => {
          setPdfLoading(false);
          setWordLoading(false);
        },
      );
    } else {
      const apiMethod =
        caseDetail.status === CaseStatusEnum.TO_BE_APPROVED
          ? ReportService.previewApprovalReport
          : ReportService.previewReport;
      apiMethod(
        caseDetail.id,
        reportId ?? 0,
        (data: any) => {
          const url = window.URL.createObjectURL(
            new Blob([data], { type: "application/pdf" }),
          );
          const pdfWindow = window.open();
          if (pdfWindow) {
            pdfWindow.location.href = url;
            pdfWindow.document.title = "report";
          }
        },
        () => {},
        () => {
          setPdfLoading(false);
          setWordLoading(false);
        },
      );
    }
  };

  const handleDownloadDirectReport = (reportId?: number) => () => {
    if (!caseDetail?.id) return;
    ReportService.downloadDirectReport(
      caseDetail.id,
      reportId ?? 0,
      (reportUrl: string) => {
        window.open(reportUrl, "_blank");
      },
      () => {},
      () => {
        setPdfLoading(false);
        setWordLoading(false);
      },
    );
  };
  // ── Ctrl+S warning message used across template & report actions ──
  const CTRL_S_WARNING =
    "Press Ctrl + S to save your document. Otherwise Unsaved changes will be lost. If you have already pressed Ctrl + S, click Continue.";

  const handleTemplateChange = async (value: any) => {
    const template = reportTemplates.find((t) => t.id === value);
    if (!template) return;

    showSaveConfirm(
      "Save Before Selecting Template",
      CTRL_S_WARNING,
      async () => {
        const existingContent = currentContentRef.current;
        const newContent =
          existingContent && existingContent !== "<p></p>"
            ? existingContent + "<br/>" + template.content
            : template.content;
        try {
          await loadHtmlIntoEditor(newContent, false);
          // Auto-save to DB immediately (updates formValues)
          handleSaveDraftWithContent(newContent, formValuesRef.current);
        } catch (err) {
          console.error("Failed to load template:", err);
          Notification({
            message: "Failed to load template into editor",
            type: NotificationTypes.ERROR,
          });
        }
      },
    );
  };

  const smartReporting = () => {
    if (!isAiReportingAllowed) {
      Notification({
        message:
          "AI reporting feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return;
    }
    setIsModalOpen(true);
  };

  const generateGeminiReport = async () => {
    if (!textareaValue.trim().length) {
      alert("Please enter key findings or observations.");
      return;
    }

    showSaveConfirm(
      "Save Before Generating Smart Report",
      CTRL_S_WARNING,
      async () => {
        setAiReportLoading(true);
        setIsModalOpen(false);
        const report = Object.assign(new Report(), formValuesRef.current);
        report.content = currentContentRef.current;
        ReportService.generateGeminiReport(
          report,
          currentContentRef.current,
          textareaValue,
          [],
          async (generatedText: string) => {
            try {
              generatedText = generatedText.replace(/```html|```/g, "").trim();
              await loadHtmlIntoEditor(generatedText, false);
              storeAiReportingSession();
              setAiReportLoading(false);
              setDisableUsgReporting(false);
              Notification({
                message: "Smart Report generated successfully!",
                type: NotificationTypes.SUCCESS,
              });
            } catch (err) {
              setCurrentContent(generatedText);
              currentContentRef.current = generatedText;
              setActiveHtmlContent(generatedText);
              setAiReportLoading(false);
              Notification({
                message: "Report generated but failed to load into editor.",
                type: NotificationTypes.ERROR,
              });
            }
          },
          () => {
            setAiReportLoading(false);
            Notification({
              message: "Smart Report generation failed",
              type: NotificationTypes.ERROR,
            });
          },
          () => {
            setAiReportLoading(false);
          },
        );
      },
    );
  };

  // USG Report
  const generateUsgReport = () => {
    if (!isAiDiagnosisEnabled) {
      Notification({
        message:
          "AI reporting feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return;
    }
    const content = currentContentRef.current;
    if (!content || content === "<p></p>") {
      Notification({
        message: "Please select a report template for generating USG report",
        type: NotificationTypes.ERROR,
      });
      return;
    }

    showSaveConfirm("Save Before Generating USG Report", CTRL_S_WARNING, () => {
      setUsgReportLoading(true);
      setDisableAiReporting(true);

      const report = Object.assign(new Report(), formValuesRef.current);
      report.content = content;

      ReportService.generateUsgReport(
        caseDetail?.id,
        report,
        content,
        async (generatedText: string) => {
          try {
            generatedText = generatedText.replace(/```html|```/g, "").trim();
            await loadHtmlIntoEditor(generatedText, false);
            storeAiReportingSession();
            setUsgReportLoading(false);
            setDisableAiReporting(false);
            Notification({
              message: "USG Report generated successfully!",
              type: NotificationTypes.SUCCESS,
            });
          } catch (err) {
            setCurrentContent(generatedText);
            currentContentRef.current = generatedText;
            setActiveHtmlContent(generatedText);
            setUsgReportLoading(false);
            setDisableAiReporting(false);
            Notification({
              message: "USG report generated but failed to load into editor.",
              type: NotificationTypes.ERROR,
            });
          }
        },
        () => {
          setUsgReportLoading(false);
          setDisableAiReporting(false);
          Notification({
            message: "USG Report generation failed",
            type: NotificationTypes.ERROR,
          });
        },
        () => {
          setUsgReportLoading(false);
          setDisableAiReporting(false);
        },
      );
    });
  };

  // Organ select
  const handleOrganSelect = (value: any) => {
    setSelectedOrgan(value);
    setSelectedOrganValue(value);
    setSelectedDetailValue(null);
    // @ts-ignore
    setSubOptions(subOptionsMap[value] || []);
  };

  // In OnlyOffice we can't insert at cursor like CKEditor, so we append to end of content
  const handlePathologyDetail = (value: any) => {
    if (value !== undefined) {
      setSelectedDetailValue(value);
      const currentHtml = currentContentRef.current || "<p></p>";
      // Append the selected pathology text to end of the document
      const updated = currentHtml.endsWith("</p>")
        ? currentHtml.replace(/<\/p>$/, " " + value + "</p>")
        : currentHtml + " " + value;
      setCurrentContent(updated);
      currentContentRef.current = updated;
      setHasUnsavedChanges(true);
    }
  };

  const stopSpeechRecognition = () => {
    setIsMicLoading(true);
    if (socket && socket.readyState !== 3) {
      socket.close();
      syncVoiceUsage();
      storeVoiceToTextSession();
      lastSyncedRef.current = 0;
    }
    setIsMicLoading(false);
    setIsMuted(true);
    setIsRunning(false);
    setSessionTimer(0);
  };

  const startSpeechRecognition = () => {
    if (!isVoiceToTextAllowed) {
      Notification({
        message:
          "Voice to text feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return;
    }
    let silenceTimeout: NodeJS.Timeout | null = null;
    setVoiceLoading(true);
    setIsMicLoading(true);
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      if (!("MediaRecorder" in window)) return alert("Browser not supported");
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });
      socket = new WebSocket(
        process.env.REACT_APP_VOICE_BASE_URL! + ApiRoutes.VOICE_TO_TEXT_ENABLE,
      );
      socket.onopen = () => {
        setIsRunning(true);
        setIsMuted(false);
        setIsMicLoading(false);
        Notification({
          message: "Speech detection activated!",
          type: NotificationTypes.SUCCESS,
        });
        mediaRecorder.addEventListener("dataavailable", async (event: any) => {
          if (event.data.size > 0 && socket!.readyState === 1)
            socket!.send(event.data);
        });
        mediaRecorder.start(1000);
        resetSilenceTimeout();
      };
      socket.onmessage = (message) => {
        if (message.data) setTextareaValue((prev) => prev + " " + message.data);
        resetSilenceTimeout();
      };
      socket.onclose = () => {
        mediaRecorder.stop();
        stream.getTracks().forEach((t) => t.stop());
        if (silenceTimeout) {
          clearTimeout(silenceTimeout);
          silenceTimeout = null;
        }
        Notification({
          message: "Speech detection ended!",
          type: NotificationTypes.SUCCESS,
        });
        setIsMuted(true);
        setIsRunning(false);
        setSessionTimer(0);
        setVoiceLoading(false);
      };
      socket.onerror = (error) => console.log({ event: "onerror", error });
    });
    function resetSilenceTimeout() {
      if (silenceTimeout) clearTimeout(silenceTimeout);
      silenceTimeout = setTimeout(() => {
        stopSpeechRecognition();
        syncVoiceUsage();
        setSessionTimer(0);
      }, 30000);
    }
  };

  const startAugnitoSpeechRecognition = () => {
    if (!isVoiceToTextAllowed) {
      Notification({
        message:
          "Voice to text feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return;
    }
    let silenceTimeout: NodeJS.Timeout | null = null;
    setVoiceLoading(true);
    setIsMicLoading(true);
    let audioContext: AudioContext;
    let processor: ScriptProcessorNode;
    let source: MediaStreamAudioSourceNode;
    let mediaStream: MediaStream;
    socket = new WebSocket(
      process.env.REACT_APP_VOICE_BASE_URL! +
        ApiRoutes.AUGNITO_VOICE_TO_TEXT_ENABLE,
    );
    socket.binaryType = "arraybuffer";
    socket.onopen = async () => {
      setIsRunning(true);
      setIsMuted(false);
      setIsMicLoading(false);
      Notification({
        message: "Speech detection activated!",
        type: NotificationTypes.SUCCESS,
      });
      audioContext = new AudioContext({ sampleRate: 16000 });
      mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      source = audioContext.createMediaStreamSource(mediaStream);
      processor = audioContext.createScriptProcessor(4096, 1, 1);
      processor.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++)
          pcm16[i] = Math.max(-1, Math.min(1, input[i])) * 0x7fff;
        // @ts-ignore
        if (socket.readyState === WebSocket.OPEN) socket.send(pcm16.buffer);
      };
      source.connect(processor);
      processor.connect(audioContext.destination);
      resetSilenceTimeout();
    };
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (!data.text) return;
      if (data.type === "partial") setLiveTranscript(data.text);
      if (data.type === "final") {
        setBaseValue((prev) => (prev + " " + data.text).trim());
        setTextareaValue((prev) =>
          (prev + " " + data.text).replace(/\s+/g, " ").trim(),
        );
        setLiveTranscript("");
      }
      resetSilenceTimeout();
    };
    socket.onclose = () => {
      Notification({
        message: "Speech detection ended!",
        type: NotificationTypes.SUCCESS,
      });
      processor?.disconnect();
      source?.disconnect();
      audioContext?.close();
      if (mediaStream) mediaStream.getTracks().forEach((t) => t.stop());
      if (silenceTimeout) {
        clearTimeout(silenceTimeout);
        silenceTimeout = null;
      }
      setIsMuted(true);
      setIsRunning(false);
      setSessionTimer(0);
      setVoiceLoading(false);
    };
    socket.onerror = (error) => console.error("WebSocket error:", error);
    function resetSilenceTimeout() {
      if (silenceTimeout) clearTimeout(silenceTimeout);
      silenceTimeout = setTimeout(() => {
        stopSpeechRecognition();
        syncVoiceUsage();
        setSessionTimer(0);
      }, 30000);
    }
  };

  const handleMicClick = () => {
    if (voiceLoading) {
      stopSpeechRecognition();
      return;
    }
    if (isDeepgram) startSpeechRecognition();
    else startAugnitoSpeechRecognition();
  };

  // ── Derived: show USG section when scan type is US and feature enabled ──
  const showAiDiagnosisSection =
    isAiDiagnosisEnabled &&
    aiDiagnosisTypes.includes(caseDetail?.scanTypeName as string);

  // Loading
  if (pageLoading) {
    return (
      <>
        <style>{`
        * { box-sizing: border-box; }
        body, html {
          margin: 0 !important; padding: 0 !important;
          overflow: hidden !important; height: 100%; width: 100%;
        }
        .app-header, .app-sidebar, header, nav, .sidebar,
        #appRoutes > .app-header, #appRoutes > .app-sidebar { display: none !important; }
        #appRoutes { padding: 0 !important; margin: 0 !important; }
      `}</style>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            width: "100vw",
            position: "fixed",
            top: 0,
            left: 0,
            background: "#fff",
            zIndex: 9999,
          }}
        >
          <Spin size="large" tip="Loading report..." />
        </div>
      </>
    );
  }

  // Publish button
  const renderPublishButton = () => {
    if (!formValues?.id || formValues?.status) return null;
    const doctorUser = user as Doctor | undefined;
    const needsApproval = doctorUser?.approvalRequired && (caseDetail?.approvalRequiredForCase ?? true);
    if (needsApproval) {
      return (
        <Button
          type="primary"
          loading={publishLoading}
          onClick={handleSendForApproval}
        >
          Send for Approval
        </Button>
      );
    }
    if (
      caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED &&
      userRole === UserRoleEnum.ADMIN
    ) {
      return (
        <Button
          type="primary"
          loading={publishLoading}
          onClick={handleApproveAndPublishReport}
        >
          Approve and Publish Report
        </Button>
      );
    }
    return (
      <Button
        type="primary"
        loading={publishLoading}
        icon={<CheckOutlined />}
        onClick={handlePublishReport}
      >
        Save & Publish
      </Button>
    );
  };

  const voiceTooltip = (
    <div>
      <p>
        <strong>How to use Voice-to-Text?</strong>
      </p>
      <ul style={{ paddingLeft: 16 }}>
        <li>Use a microphone for better accuracy</li>
        <li>Speak clearly and pause between words</li>
        <li>
          Say <code>"comma, colon, semicolon"</code> → <code>, : ;</code>
        </li>
        <li>
          Say <code>"full stop"</code> → <code>.</code>
        </li>
        <li>
          Say <code>"next line"</code> → <code>new line</code>
        </li>
      </ul>
    </div>
  );

  const documentTitle = `${caseDetailId}.docx`;

  // RENDER
  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body, html {
          margin: 0 !important; padding: 0 !important;
          overflow: hidden !important; height: 100%; width: 100%;
        }
        .app-header, .app-sidebar, header, nav, .sidebar,
        #appRoutes > .app-header, #appRoutes > .app-sidebar { display: none !important; }
        #appRoutes { padding: 0 !important; margin: 0 !important; }
        .onlyoffice-page {
          display: flex; flex-direction: column;
          height: 100vh; width: 100vw;
          overflow: hidden; position: fixed; top: 0; left: 0;
        }
        .onlyoffice-toolbar { padding: 8px 16px; background: #fff; flex-shrink: 0; }
        .onlyoffice-toolbar .ant-select-selector {
          padding: 8px 14px !important; border-radius: 6px !important;
          min-height: 40px !important; display: flex; align-items: center;
        }
        .onlyoffice-editor-area {
          flex: 1; overflow: hidden; position: relative; min-height: 0; width: 100%;
        }
      `}</style>

      {/* ── Ctrl+S Warning Modal ── */}
      <Modal
        visible={confirmModal.visible}
        title={
          <span>
            <ExclamationCircleOutlined
              style={{ color: "#faad14", marginRight: 8 }}
            />
            {confirmModal.title}
          </span>
        }
        okText="Continue"
        cancelText="Cancel"
        onOk={handleConfirmModalOk}
        onCancel={handleConfirmModalCancel}
        width={480}
        style={{ top: 30 }}
      >
        <p
          style={{
            fontSize: 14,
            color: "#595959",
            lineHeight: 1.6,
          }}
        >
          {confirmModal.message}
        </p>
      </Modal>

      <div className="onlyoffice-page">
        {/* ── ROW 1: Patient info | Upload + Preview ── */}
        <div
          className="onlyoffice-toolbar"
          style={{
            padding: "6px 16px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 16,
              justifyContent: "center",
            }}
          >
            <strong style={{ fontSize: 17, whiteSpace: "nowrap" }}>
              {caseDetail?.patientName} | {caseDetail?.age} /{" "}
              {caseDetail?.gender} | {caseDetail?.scanTypeName}
            </strong>

            {caseDetail?.cosignEnabled && caseDetail?.cosignContributors && caseDetail.cosignContributors.length > 0 && (
              <div style={{ fontSize: 12, color: "#595959", marginTop: 2 }}>
                Drafted by: {caseDetail.cosignContributors.map((c) => c.name).join(", ")}
              </div>
            )}

            {cosignLockedBy && (
              <div style={{ fontSize: 12, color: "#ad4e00", marginTop: 4, fontWeight: 500 }}>
                🔒 {cosignLockedBy} is currently editing this report — opened in read-only mode
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {!formValues?.status && (
                <UploadDirectReportButton
                  caseDetail={caseDetail}
                  onSuccess={(report: Report) => {
                    setFormValues(report);
                    formValuesRef.current = report;
                    setHasUnsavedChanges(false);
                  }}
                />
              )}

              {formValues?.id &&
                (formValues?.status ? (
                  <Popover
                    trigger="click"
                    content={
                      <Menu mode="vertical">
                        {formValues?.directReportUpload ? (
                          <Menu.Item
                            key="dl"
                            onClick={handleDownloadDirectReport(formValues?.id)}
                          >
                            Download Uploaded Report
                          </Menu.Item>
                        ) : (
                          <>
                            <Menu.Item key="view" onClick={handleViewReport}>
                              View Report
                            </Menu.Item>
                            <Menu.Item
                              key="pdf-lh"
                              onClick={handleDownloadReport(
                                formValues?.status,
                                DownloadFileTypeEnum.PDF,
                                true,
                                formValues?.id,
                              )}
                            >
                              Download with Letterhead
                            </Menu.Item>
                            <Menu.Item
                              key="pdf"
                              onClick={handleDownloadReport(
                                formValues?.status,
                                DownloadFileTypeEnum.PDF,
                                false,
                                formValues?.id,
                              )}
                            >
                              Download without Letterhead
                            </Menu.Item>
                            <Menu.Item
                              key="word"
                              onClick={handleDownloadReport(
                                formValues?.status,
                                DownloadFileTypeEnum.WORD,
                                false,
                                formValues?.id,
                              )}
                            >
                              Download as Word
                            </Menu.Item>
                          </>
                        )}
                      </Menu>
                    }
                  >
                    <Button type="primary" loading={pdfLoading}>
                      <DownloadOutlined /> Download
                    </Button>
                  </Popover>
                ) : (
                  <Tooltip title="Preview PDF">
                    <Button
                      icon={<EyeOutlined />}
                      loading={pdfLoading}
                      onClick={handleDownloadReport(
                        formValues?.status,
                        DownloadFileTypeEnum.PDF,
                        false,
                        formValues?.id,
                      )}
                    >
                      Preview
                    </Button>
                  </Tooltip>
                ))}
            </div>
          </div>
        </div>

        {/* ── ROW 2: Template + RadAI | Save Draft + Publish ── */}
        <div className="onlyoffice-toolbar">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ width: 300 }} />

            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 600 }}>
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Template"
                  style={{ width: "100%" }}
                  filterOption={(input, option) =>
                    (option?.label as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onSearch={(val) => setSearchTerm(val)}
                  onChange={(value) => handleTemplateChange(value)}
                  options={reportTemplateOptions}
                />
              </div>

              {isAiReportingEnabled && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Button
                    loading={aiReportLoading}
                    disabled={disableAiReporting}
                    onClick={() => {
                      if (!isModalOpen) smartReporting();
                      else {
                        setIsModalOpen(false);
                        setIsMicLoading(false);
                        stopSpeechRecognition();
                      }
                    }}
                  >
                    {!isModalOpen ? "Try RadAI Assist" : "Close RadAI Assist"}
                  </Button>
                  <Tooltip
                    title={
                      <div>
                        <p>
                          <strong>How to use RadAI Assist?</strong>
                        </p>
                        <ul style={{ paddingLeft: 16 }}>
                          <li>
                            Start by choosing a report template that fits your
                            case
                          </li>
                          <li>
                            Click on <em>Try RadAI Assist</em>
                          </li>
                          <li>Enter your key findings or observations</li>
                          <li>
                            Click on <em>Generate Smart Report</em>
                          </li>
                          <li>
                            Let RadAI Assist craft a detailed, intelligent
                            report for you
                          </li>
                        </ul>
                      </div>
                    }
                    placement="top"
                  >
                    <QuestionCircleOutlined
                      style={{
                        fontSize: 16,
                        marginLeft: 8,
                        cursor: "pointer",
                        color: "#1747E9",
                      }}
                    />
                  </Tooltip>
                  <span>
                    {aiCreditsUsed} / {aiTotalCredits} credits
                  </span>
                </div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {!formValues?.status && (
                <Button
                  type="primary"
                  loading={formLoading}
                  icon={<SaveOutlined />}
                  disabled={!!cosignLockedBy}
                  onClick={handleSaveDraft}
                >
                  Save Draft
                </Button>
              )}
              {!cosignLockedBy && renderPublishButton()}
            </div>
          </div>

          {/* ── USG Organ Row — */}
          {showAiDiagnosisSection && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 8,
                flexWrap: "wrap",
              }}
            >
              {/* Organ dropdown */}
              <div style={{ minWidth: 200, flex: "0 0 auto" }}>
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Organ"
                  style={{ width: "100%" }}
                  value={selectedOrganValue}
                  filterOption={(input, option) =>
                    (option?.label as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value) => handleOrganSelect(value)}
                  options={organOptions}
                />
              </div>

              {/* Pathology detail dropdown — disabled until organ selected */}
              <div style={{ minWidth: 280, flex: "1 1 auto", maxWidth: 400 }}>
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Pathology Detail"
                  style={{ width: "100%" }}
                  value={selectedDetailValue}
                  disabled={!selectedOrgan}
                  filterOption={(input, option) =>
                    (option?.label as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value) => handlePathologyDetail(value)}
                  options={subOptions}
                />
              </div>

              {/* Generate USG Report button */}
              <Button
                type="primary"
                loading={usgReportLoading}
                disabled={disableUsgReporting}
                onClick={generateUsgReport}
                style={{ flexShrink: 0 }}
              >
                Generate USG Report
              </Button>
            </div>
          )}

          {/* RadAI panel */}
          {isModalOpen && (
            <Row gutter={[10, 8]} style={{ marginTop: 8 }}>
              <Col span={24}>
                <Input.TextArea
                  rows={4}
                  placeholder="Enter your key findings or observations"
                  value={
                    isDeepgram
                      ? textareaValue
                      : baseValue + (liveTranscript || "")
                  }
                  style={{
                    color: !isDeepgram && liveTranscript ? "#999" : "inherit",
                  }}
                  onChange={(e) => {
                    if (isDeepgram) setTextareaValue(e.target.value);
                    else {
                      setBaseValue(e.target.value);
                      setTextareaValue(e.target.value);
                    }
                  }}
                />
              </Col>
              <Col
                span={24}
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                {isVoiceToTextEnabled && (
                  <>
                    <Button
                      type="primary"
                      loading={isMicLoading}
                      onClick={handleMicClick}
                    >
                      {isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
                    </Button>
                    <Tooltip title={voiceTooltip} placement="top">
                      <QuestionCircleOutlined
                        style={{
                          fontSize: 16,
                          color: "#1747E9",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                    <span>
                      {formatTime(timer)} / {formatTime(totalTimer)}
                    </span>
                  </>
                )}
                <Button
                  type="primary"
                  loading={aiReportLoading}
                  onClick={generateGeminiReport}
                >
                  Generate Smart Report
                </Button>
              </Col>
            </Row>
          )}

          {(aiReportLoading || usgReportLoading) && (
            <div style={{ textAlign: "center", padding: 10 }}>
              <Spin tip="Radiolinq AI Assist is preparing your report..." />
            </div>
          )}
        </div>

        {/* ── EDITOR ── */}
        <div className="onlyoffice-editor-area">
          <OnlyOfficeEditor
            key={editorVersion}
            htmlContent={activeHtmlContent}
            reportId={fixedKey}
            documentServerUrl={documentServerUrl}
            documentTitle={documentTitle}
            onContentChange={(content) => {
              currentContentRef.current = content;
              setCurrentContent(content);

              if (isForceSavePollingRef.current) {
                // Polling delivered fresh HTML after forceSave/Ctrl+S inside editor
                // Auto-save it and clear the unsaved flag — NOT a user edit
                isForceSavePollingRef.current = false;
                handleSaveDraftWithContent(content, formValuesRef.current);
              } else {
                // Real user edit
                setHasUnsavedChanges(true);
              }
            }}
            editorInstanceRef={editorInstanceRef}
          />
        </div>
      </div>
    </>
  );
};

export default AuthContainer(OnlyOfficePage);
